import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { InviteCode, ModelPlanDetail } from '@nadoutong/contracts';
import { OpsApiError } from '../../api/client.ts';
import {
  useCreatePlanFromInviteCodeDiscounts,
  useInviteCodePlanRebindPreview,
  useUpdateInviteCodePlan,
} from '../../api/inviteCodes.ts';
import { CustomerRef } from '../../components/identity/CustomerRef.tsx';
import { ModelPlanSelect } from '../../components/ModelPlanSelect.tsx';
import { coerceErrorMessage, useToast } from '../../components/Toast.tsx';
import {
  MODEL_PLAN_REBIND_ROLES,
  ROLE_OPS_ADMIN,
  hasAnyOpsRole,
} from '../../auth/opsRoles.ts';
import { useSession } from '../../hooks/useSession.tsx';
import { useT } from '../../i18n/index.ts';
import { Button, Dialog, FormField, Input, RadioGroup } from '../../ui/index.ts';
import { SkuDeltasList } from '../model-plans/preview.tsx';

/**
 * Rebind scope is a FORCED choice for admins, not a default-plus-checkbox:
 * "future only" and "migrate the code's activated customers too" are both
 * legitimate reads of 换绑, and a silent default was repeatedly misread as
 * "of course it moves everyone / of course it moves no one". A lead can only
 * ever pick the future-only arm (restate is admin-only server-side), so for
 * them it is preselected and the restate arm renders disabled-with-reason
 * instead of hidden — the option being visible is what explains the system.
 */
type RebindScope = 'future' | 'restate';

export function RebindPlanDialog({
  code,
  onClose,
}: {
  readonly code: InviteCode;
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { user } = useSession();
  const isAdmin = hasAnyOpsRole(user?.roles ?? [], [ROLE_OPS_ADMIN]);
  const canRestate = hasAnyOpsRole(user?.roles ?? [], MODEL_PLAN_REBIND_ROLES);
  const update = useUpdateInviteCodePlan();
  const migrate = useCreatePlanFromInviteCodeDiscounts();
  const [planId, setPlanId] = useState<string | null>(code.model_plan?.id ?? null);
  // Admins must choose; non-admins get the only arm they can take.
  const [scope, setScope] = useState<RebindScope | null>(canRestate ? null : 'future');
  const [error, setError] = useState<string | null>(null);
  const [migrateName, setMigrateName] = useState(`migrated: ${code.code}`);
  const [createdPlan, setCreatedPlan] = useState<ModelPlanDetail | null>(null);

  const hasLegacyDiscounts = code.rate_discounts.length > 0 && createdPlan === null;
  const unbinding = planId === null;
  const restate = scope === 'restate' && canRestate && !unbinding;
  const preview = useInviteCodePlanRebindPreview(code.id, restate);

  useEffect(() => {
    // Unbinding has no destination plan, so the scope question dissolves;
    // coming back to a plan re-asks it rather than reusing a stale answer.
    if (unbinding && scope !== null && canRestate) {
      setScope(null);
    }
  }, [unbinding, scope, canRestate]);

  function generatePlan(): void {
    const name = migrateName.trim();
    if (name.length === 0) {
      setError(t('invite_codes.rebind.from_discounts.name_required'));
      return;
    }
    setError(null);
    migrate.mutate(
      { id: code.id, body: { name } },
      {
        onSuccess: (body) => {
          setCreatedPlan(body.model_plan);
          setPlanId(body.model_plan.id);
          toast.success(
            t('invite_codes.rebind.from_discounts.toast', { name: body.model_plan.name }),
          );
        },
        onError: (err) => {
          setError(coerceErrorMessage(err));
        },
      },
    );
  }

  function submit(): void {
    if (hasLegacyDiscounts && planId !== null) {
      setError(t('invite_codes.rebind.legacy_discounts'));
      return;
    }
    if (!unbinding && scope === null) {
      setError(t('invite_codes.rebind.scope_required'));
      return;
    }
    setError(null);
    update.mutate(
      {
        id: code.id,
        body: {
          model_plan_id: planId,
          ...(restate ? { restate_activated: true } : {}),
        },
      },
      {
        onSuccess: () => {
          onClose();
          toast.success(
            planId === null
              ? t('invite_codes.rebind.toast_unbound')
              : t('invite_codes.rebind.toast'),
          );
        },
        onError: (err) => {
          if (err instanceof OpsApiError && err.code === 'INVITE_CODE_HAS_LEGACY_DISCOUNTS') {
            setError(t('invite_codes.rebind.legacy_discounts'));
            return;
          }
          setError(coerceErrorMessage(err));
        },
      },
    );
  }

  const confirmBlocked = restate && (preview.isLoading || preview.isError);

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !update.isPending && !migrate.isPending) onClose();
      }}
      title={t('invite_codes.rebind.title', { code: code.code })}
      description={t('invite_codes.rebind.description')}
      closeLabel={t('common.close')}
      size="lg"
      testId="invite-codes-rebind-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={update.isPending || migrate.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={update.isPending}
            disabled={confirmBlocked || migrate.isPending}
            data-testid="invite-codes-rebind-submit"
          >
            {t('invite_codes.rebind.submit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="m-0 rounded-lg border border-line-soft bg-well px-3 py-2 text-dense text-paper-low">
          {code.model_plan === null
            ? t('invite_codes.rebind.current_plan_none')
            : t('invite_codes.rebind.current_plan', { name: code.model_plan.name })}
        </p>

        <ModelPlanSelect
          brand={isAdmin ? code.brand : undefined}
          value={planId}
          onChange={setPlanId}
          allowNone
          fieldId="invite-codes-rebind-plan"
          label={t('invite_codes.rebind.plan_label')}
          testId="invite-codes-rebind-plan"
        />

        {unbinding ? (
          <p
            className="m-0 rounded-lg border border-line-soft bg-well px-3 py-2 text-dense text-paper-low"
            data-testid="invite-codes-rebind-unbind-note"
          >
            {t('invite_codes.rebind.unbind_note')}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            <span className="text-label text-paper-low">
              {t('invite_codes.rebind.scope_label')}
            </span>
            <RadioGroup
              value={scope ?? undefined}
              onValueChange={(next) => {
                if (next === 'future' || next === 'restate') setScope(next);
              }}
              ariaLabel={t('invite_codes.rebind.scope_label')}
              testId="invite-codes-rebind-scope"
              options={[
                {
                  value: 'future',
                  label: (
                    <span data-testid="invite-codes-rebind-future">
                      {t('invite_codes.rebind.scope_future')}
                    </span>
                  ),
                  hint: t('invite_codes.rebind.scope_future_hint'),
                },
                {
                  value: 'restate',
                  label: (
                    <span data-testid="invite-codes-rebind-restate">
                      {t('invite_codes.rebind.scope_restate')}
                    </span>
                  ),
                  hint: canRestate
                    ? t('invite_codes.rebind.scope_restate_hint')
                    : t('invite_codes.rebind.scope_restate_admin_only'),
                  disabled: !canRestate,
                },
              ]}
            />
          </div>
        )}

        {restate && preview.isLoading ? (
          <p className="m-0 text-dense text-paper-low" data-testid="invite-codes-rebind-preview-loading">
            {t('invite_codes.rebind.preview_loading')}
          </p>
        ) : null}
        {restate && preview.isError ? (
          <p
            role="alert"
            className="m-0 rounded-lg border border-bad-line bg-bad-dim/50 px-3 py-2 text-dense text-bad-bright"
            data-testid="invite-codes-rebind-preview-error"
          >
            {t('invite_codes.rebind.preview_error')}
          </p>
        ) : null}
        {restate && preview.isSuccess ? (
          <div
            className="flex flex-col gap-3"
            data-testid="invite-codes-rebind-preview"
          >
            {preview.data.orgs.length === 0 ? (
              <p className="m-0 text-dense text-paper-low">
                {t('invite_codes.rebind.preview_empty')}
              </p>
            ) : (
              <>
                <p className="m-0 text-dense text-paper">
                  {t('invite_codes.rebind.preview_orgs', { count: preview.data.orgs.length })}
                </p>
                {preview.data.orgs.map((org) => (
                  <div
                    key={org.org_id}
                    className="flex flex-col gap-1 rounded-lg border border-line-soft bg-well px-3 py-2"
                    data-testid={`invite-codes-rebind-org-${org.org_id}`}
                  >
                    <span className="font-mono text-dense text-paper">
                      <CustomerRef orgId={org.org_id} />
                      <span className="ml-2 text-paper-low">{org.current_model_plan_name}</span>
                    </span>
                    <SkuDeltasList
                      deltas={org.sku_deltas}
                      testId={`invite-codes-rebind-deltas-${org.org_id}`}
                    />
                  </div>
                ))}
              </>
            )}
          </div>
        ) : null}

        {hasLegacyDiscounts ? (
          <div
            className="flex flex-col gap-3 rounded-lg border border-warn-line bg-warn-dim/40 px-3 py-3"
            data-testid="invite-codes-rebind-legacy"
          >
            <p role="alert" className="m-0 text-dense text-warn">
              {t('invite_codes.rebind.legacy_discounts')}
            </p>
            <FormField
              label={t('invite_codes.rebind.from_discounts.name')}
              fieldId="invite-codes-from-discounts-name"
              hint={t('invite_codes.rebind.from_discounts.hint')}
            >
              <Input
                id="invite-codes-from-discounts-name"
                value={migrateName}
                onChange={(event) => setMigrateName(event.currentTarget.value)}
                data-testid="invite-codes-from-discounts-name"
              />
            </FormField>
            <div className="flex justify-end">
              <Button
                variant="secondary"
                onClick={generatePlan}
                loading={migrate.isPending}
                disabled={migrate.isPending}
                data-testid="invite-codes-from-discounts-submit"
              >
                {t('invite_codes.rebind.from_discounts.submit')}
              </Button>
            </div>
          </div>
        ) : null}

        {createdPlan !== null ? (
          <p
            className="m-0 rounded-lg border border-ok-line bg-ok-dim/40 px-3 py-2 text-dense text-ok"
            data-testid="invite-codes-from-discounts-created"
          >
            {t('invite_codes.rebind.from_discounts.created', { name: createdPlan.name })}
            <Link
              to="/market/model-plans"
              className="ml-2 text-signal transition-colors duration-120 hover:text-signal-bright"
              data-testid="invite-codes-from-discounts-open"
            >
              {t('invite_codes.rebind.from_discounts.open')}
            </Link>
          </p>
        ) : null}

        {error !== null ? (
          <p
            role="alert"
            data-testid="invite-codes-rebind-error"
            className="m-0 rounded-lg border border-bad-line bg-bad-dim/50 px-3 py-2 text-dense text-bad-bright"
          >
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
